import React from 'react';
import { NumberFormatValues, NumericFormat } from 'react-number-format';
import styled from 'styled-components';
import { ANALYTIC_PANEL_COLORS } from 'utils/constants/analyticPanelColors';

import TextInput from 'components/common/Inputs/TextInput';

export interface TimelineStage {
    id: string;
    label: string;
    description?: string;
    fieldName: string;
    value: string | number | null | undefined;
    allowNegative?: boolean;
    readOnly?: boolean;
    colorVariant?: 'success' | 'warning' | 'error';
    infoMessage?: string;
}

export interface TimelineCostsGridProps {
    stages: TimelineStage[];
    title?: string;
    totalValue?: string | number | null;
    readOnly?: boolean;
    onValueChange?: (fieldName: string, value: string) => void;
    onValueBlur?: (fieldName: string, value: string) => void;
}

const Wrapper = styled.div`
    margin-top: 20px;
`;

const Title = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: var(--color-primary);
    margin-bottom: 16px;
`;

const Grid = styled.div<{ $itemCount: number }>`
    display: grid;
    grid-template-columns: ${({ $itemCount }) => `repeat(${Math.min($itemCount, 4)}, 1fr)`};
    gap: 16px;

    @media (max-width: 1200px) {
        grid-template-columns: ${({ $itemCount }) => `repeat(${Math.min($itemCount, 2)}, 1fr)`};
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const StageCard = styled.div<{ $colorVariant?: 'success' | 'warning' | 'error' }>`
    background: var(--color-white);
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    position: relative;
    overflow: hidden;
    transition: box-shadow 0.2s ease;
    border: 2px solid ${({ $colorVariant }) => {
        if ($colorVariant === 'success') return 'var(--color-success)';
        if ($colorVariant === 'warning') return 'var(--color-warning)';
        if ($colorVariant === 'error') return 'var(--color-error)';

        return 'transparent';
    }};

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
`;

const StageDivider = styled.div<{ $stageNumber: number; $colorVariant?: 'success' | 'warning' | 'error' }>`
    height: 4px;
    background: ${({ $stageNumber, $colorVariant }) => {
        if ($colorVariant === 'success') return 'var(--color-success)';
        if ($colorVariant === 'warning') return 'var(--color-warning)';
        if ($colorVariant === 'error') return 'var(--color-error)';

        const colors = [
            'var(--color-primary)',
            ANALYTIC_PANEL_COLORS.renovation,
            ANALYTIC_PANEL_COLORS.operatingCosts,
            ANALYTIC_PANEL_COLORS.saleCosts
        ];

        return colors[$stageNumber % colors.length];
    }};
`;

const StageLabel = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-secondary);
    margin-bottom: 4px;
`;

const StageDescription = styled.div`
    font-size: 12px;
    font-weight: 400;
    color: var(--color-text-secondary);
    opacity: 0.7;
    margin-bottom: 8px;
    line-height: 1.4;
`;

const InputWrapper = styled.div<{ $readOnly?: boolean }>`
    margin-top: 24px;

    ${({ $readOnly }) => $readOnly && `
        span, input {
            cursor: default !important;
        }
    `}
`;

const InfoText = styled.div<{ $colorVariant?: 'success' | 'warning' | 'error' }>`
    font-size: 12px;
    font-weight: 500;
    margin-top: 8px;
    color: ${({ $colorVariant }) => {
        if ($colorVariant === 'error') return 'var(--color-error)';
        if ($colorVariant === 'warning') return 'var(--color-warning)';
        if ($colorVariant === 'success') return 'var(--color-success)';

        return 'var(--color-text-secondary)';
    }};
`;

export const TimelineCostsGrid = ({ stages, title, readOnly, onValueChange, onValueBlur }: TimelineCostsGridProps) => {
    const handleValueChange = (fieldName: string, values: NumberFormatValues) => {
        onValueChange?.(fieldName, values.value);
    };

    const handleBlur = (fieldName: string, value: string) => {
        onValueBlur?.(fieldName, value);
    };

    const handleFocus = (e: React.FocusEvent<Element>) => {
        const target = e.target as HTMLInputElement;

        if (target && typeof target.select === 'function') {
            target.select();
        }
    };

    return (
        <Wrapper>
            {title && <Title>{title}</Title>}
            <Grid $itemCount={stages.length}>
                {stages.map((stage, index) => <StageCard key={stage.id} $colorVariant={stage.colorVariant}>
                    <StageLabel>{stage.label}</StageLabel>
                    {stage.description && <StageDescription>{stage.description}</StageDescription>}
                    <StageDivider $stageNumber={index} $colorVariant={stage.colorVariant} />
                    <InputWrapper $readOnly={readOnly ?? stage.readOnly}>
                        <NumericFormat
                            value={stage.value?.toString() || ''}
                            onValueChange={(values) => handleValueChange(stage.fieldName, values)}
                            onBlur={() => handleBlur(stage.fieldName, stage.value?.toString() || '')}
                            onFocus={handleFocus}
                            suff="zł"
                            placeholder="0"
                            customInput={TextInput}
                            thousandSeparator=" "
                            decimalSeparator=","
                            decimalScale={2}
                            allowNegative={stage.allowNegative ?? false}
                            readOnly={readOnly ?? stage.readOnly}
                        />
                        {stage.infoMessage &&
                            <InfoText $colorVariant={stage.colorVariant}>
                                {stage.infoMessage}
                            </InfoText>
                        }
                    </InputWrapper>
                </StageCard>)}
            </Grid>
        </Wrapper>
    );
};
